import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabase';

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    username: '',
    bio: '',
    avatar_icon: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [usernameError, setUsernameError] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  // Avatar icon options - Fixed to use only available icons
  const avatarIcons = [
    { id: 'FiUser', icon: FiIcons.FiUser, name: 'Default User' },
    { id: 'FiSmile', icon: FiIcons.FiSmile, name: 'Smile' },
    { id: 'FiHeart', icon: FiIcons.FiHeart, name: 'Heart' },
    { id: 'FiStar', icon: FiIcons.FiStar, name: 'Star' },
    { id: 'FiSun', icon: FiIcons.FiSun, name: 'Sun' },
    { id: 'FiMoon', icon: FiIcons.FiMoon, name: 'Moon' },
    { id: 'FiZap', icon: FiIcons.FiZap, name: 'Lightning' },
    { id: 'FiFeather', icon: FiIcons.FiFeather, name: 'Feather' },
    { id: 'FiMusic', icon: FiIcons.FiMusic, name: 'Music' },
    { id: 'FiCamera', icon: FiIcons.FiCamera, name: 'Camera' },
    { id: 'FiBook', icon: FiIcons.FiBook, name: 'Book' },
    { id: 'FiGlobe', icon: FiIcons.FiGlobe, name: 'Globe' },
    { id: 'FiCompass', icon: FiIcons.FiCompass, name: 'Compass' },
    { id: 'FiAnchor', icon: FiIcons.FiAnchor, name: 'Anchor' },
    { id: 'FiGift', icon: FiIcons.FiGift, name: 'Gift' },
    { id: 'FiCoffee', icon: FiIcons.FiCoffee, name: 'Coffee' },
    { id: 'FiUmbrella', icon: FiIcons.FiUmbrella, name: 'Umbrella' },
    { id: 'FiHeadphones', icon: FiIcons.FiHeadphones, name: 'Headphones' },
    { id: 'FiPenTool', icon: FiIcons.FiPenTool, name: 'Pen' }
  ];

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [user, navigate]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles_sb2847x9')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setProfile({
          username: data.username || '',
          bio: data.bio || '',
          avatar_icon: data.avatar_icon || 'FiUser'
        });
      } else {
        // Create initial profile if it doesn't exist
        const { error: insertError } = await supabase
          .from('profiles_sb2847x9')
          .insert([{
            id: user.id,
            username: user.email.split('@')[0],
            bio: '',
            avatar_icon: 'FiUser'
          }]);

        if (!insertError) {
          setProfile({
            username: user.email.split('@')[0],
            bio: '',
            avatar_icon: 'FiUser'
          });
        }
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkUsernameAvailability = async (username) => {
    // Skip check if username hasn't changed
    if (!username || username === profile.username) {
      setUsernameError(null);
      return true;
    }
    
    try {
      setCheckingUsername(true);
      const { data, error } = await supabase
        .from('profiles_sb2847x9')
        .select('id')
        .eq('username', username)
        .neq('id', user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        setUsernameError('Username is already taken. Please choose a different one.');
        return false;
      } else {
        setUsernameError(null);
        return true;
      }
    } catch (err) {
      console.error('Error checking username:', err);
      setUsernameError('Error checking username availability');
      return false;
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setProfile({ ...profile, username: newUsername });

    // Debounce username check
    setTimeout(() => {
      checkUsernameAvailability(newUsername);
    }, 500);
  };

  const handleSave = async () => {
    if (!profile.username.trim()) {
      setError('Username is required');
      return;
    }

    if (usernameError) {
      setError('Please fix the username error before saving');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Check username availability one more time before saving
      const isUsernameAvailable = await checkUsernameAvailability(profile.username);
      if (!isUsernameAvailable) {
        setError('This username is already taken. Please choose a different one.');
        return;
      }

      const { error } = await supabase
        .from('profiles_sb2847x9')
        .upsert({
          id: user.id,
          username: profile.username.trim(),
          bio: profile.bio.trim(),
          avatar_icon: profile.avatar_icon,
          updated_at: new Date().toISOString()
        });

      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation
          setError('This username is already taken. Please choose a different one.');
        } else {
          throw error;
        }
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const getSelectedIcon = () => {
    const selected = avatarIcons.find(icon => icon.id === profile.avatar_icon);
    return selected || avatarIcons[0];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading profile...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="profile-page bg-white rounded-3xl shadow-dreamy p-6 max-w-2xl mx-auto"
    >
      <div className="flex justify-between items-center mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
          onClick={handleBack}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-lg" />
          Back to Dashboard
        </motion.button>
        <h1 className="text-2xl font-display font-bold text-purple-700">
          My Profile
        </h1>
        <div className="w-[140px]"></div> {/* Spacer for alignment */}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
          <p>Profile updated successfully!</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Avatar Selection */}
        <div>
          <label className="block text-purple-700 font-medium mb-3">
            Choose Your Avatar
          </label>
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-purple-100 p-4 rounded-full">
              <SafeIcon icon={getSelectedIcon().icon} className="text-3xl text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-700">{getSelectedIcon().name}</p>
              <p className="text-sm text-gray-500">This will be your profile picture</p>
            </div>
          </div>
          <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
            {avatarIcons.map((iconData) => (
              <motion.button
                key={iconData.id}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`p-3 rounded-xl border-2 transition-all ${
                  profile.avatar_icon === iconData.id
                    ? 'border-purple-500 bg-purple-100'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
                onClick={() => setProfile({ ...profile, avatar_icon: iconData.id })}
                title={iconData.name}
              >
                <SafeIcon icon={iconData.icon} className="text-xl text-gray-600" />
              </motion.button>
            ))}
          </div>
        </div>

        {/* Username */}
        <div>
          <label htmlFor="username" className="block text-purple-700 font-medium mb-2">
            Username
          </label>
          <div className="relative">
            <input
              type="text"
              id="username"
              value={profile.username}
              onChange={handleUsernameChange}
              className={`w-full px-4 py-2 rounded-xl border focus:ring focus:ring-purple-300 focus:ring-opacity-50 ${
                usernameError
                  ? 'border-red-400 focus:border-red-400'
                  : 'border-purple-200 focus:border-purple-400'
              }`}
              placeholder="Enter your username"
              disabled={saving}
            />
            {checkingUsername && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>
              </div>
            )}
          </div>
          {usernameError && (
            <p className="text-red-600 text-sm mt-1">{usernameError}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Your username must be unique and will be visible to other users
          </p>
        </div>

        {/* Bio */}
        <div>
          <label htmlFor="bio" className="block text-purple-700 font-medium mb-2">
            Bio
          </label>
          <textarea
            id="bio"
            value={profile.bio}
            onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
            className="w-full px-4 py-2 rounded-xl border border-purple-200 focus:border-purple-400 focus:ring focus:ring-purple-300 focus:ring-opacity-50"
            placeholder="Tell the community about yourself..."
            rows="4"
            disabled={saving}
          />
          <p className="text-xs text-gray-500 mt-1">
            Share a bit about yourself with the community
          </p>
        </div>

        {/* User Info */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="font-medium text-gray-700 mb-2">Account Information</h3>
          <div className="text-sm text-gray-600">
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Member since:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2"
          onClick={handleSave}
          disabled={saving || checkingUsername || usernameError}
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving...
            </>
          ) : (
            <>
              <SafeIcon icon={FiIcons.FiSave} className="text-lg" />
              Save Profile
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ProfilePage;